import React, { useEffect, useState } from "react";
import { getAllDepartments, getAllTicketSubjects, createTicket } from "../services/ticketService";

const DEFAULT_STATUS_ID = "fb939613-6149-4799-9837-a6ae823b8983";
const DEFAULT_TASK_WEIGHT = 100;

interface Department {
  id: string;
  name: string;
  description: string;
}

interface Subject {
  id: string;
  name: string;
  description: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export default function CreateTicketModal({ isOpen, onClose, onCreated }: Props) {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [subjects, setSubjects]       = useState<Subject[]>([]);
  const [title, setTitle]             = useState("");
  const [note, setNote]               = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [subjectId, setSubjectId]     = useState("");
  const [priority, setPriority]       = useState(1);
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);
  const [tasks, setTasks]             = useState<{ title: string; weight: number }[]>([]);
  const [taskInput, setTaskInput]     = useState("");
  const [taskWeightInput, setTaskWeightInput] = useState(String(DEFAULT_TASK_WEIGHT));
  const [showTasksPopup, setShowTasksPopup] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState("");

  const addTask = () => {
    const trimmed = taskInput.trim();
    if (!trimmed) return;
    const parsedWeight = parseInt(taskWeightInput, 10);
    const weight = Number.isFinite(parsedWeight) && parsedWeight > 0 ? parsedWeight : DEFAULT_TASK_WEIGHT;
    setTasks((prev) => [...prev, { title: trimmed, weight }]);
    setTaskInput("");
    setTaskWeightInput(String(DEFAULT_TASK_WEIGHT));
  };
  const removeTask = (index: number) => setTasks((prev) => prev.filter((_, i) => i !== index));

  useEffect(() => {
    if (!isOpen) return;
    // reset
    setTitle(""); setNote(""); setDepartmentId(""); setSubjectId(""); setPriority(1);
    setTasks([]); setTaskInput(""); setTaskWeightInput(String(DEFAULT_TASK_WEIGHT)); setShowTasksPopup(false); setError("");
    getAllDepartments()
      .then((res) => {
        const list: Department[] = res.data ?? [];
        setDepartments(list);
        if (list.length > 0) setDepartmentId(list[0].id);
      })
      .catch(() => {});
    getAllTicketSubjects()
      .then((res) => setSubjects(res.data ?? []))
      .catch(() => {});
  }, [isOpen]);

  const isFormValid = !!title.trim() && !!departmentId && !!subjectId && !!note.trim();

  const handleSubmit = async () => {
    if (!title.trim())  { setError("Başlık zorunludur."); return; }
    if (!departmentId)  { setError("Lütfen bir departman seçin."); return; }
    if (!subjectId)     { setError("Lütfen bir konu seçin."); return; }
    if (!note.trim())   { setError("Açıklama zorunludur."); return; }
    setSaving(true);
    setError("");
    try {
      await createTicket({
        title: title.trim(),
        note: note.trim(),
        ticketStatusId: DEFAULT_STATUS_ID,
        ticketDepartmentId: departmentId,
        ticketSubjectId: subjectId,
        priority,
        tasks: tasks.map((task) => ({ title: task.title, weight: task.weight })),
      });
      onCreated();
      onClose();
    } catch {
      setError("Ticket oluşturulamadı. Lütfen tekrar deneyin.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const activeStar = hoveredStar ?? priority;

  return (
    <>
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: "20px",
          width: "92%", maxWidth: "520px",
          boxShadow: "0 12px 48px rgba(0,0,0,0.22)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "20px 24px 18px",
          background: "linear-gradient(135deg, #6366f1, #4f46e5)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{
              width: "36px", height: "36px", borderRadius: "10px",
              background: "rgba(255,255,255,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="bi bi-plus-circle-fill text-white" style={{ fontSize: "1.1rem" }}></i>
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "#fff" }}>Yeni Ticket Oluştur</div>
              <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.75)" }}>Destek talebinizi iletin</div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.15)", border: "none",
              borderRadius: "8px", width: "32px", height: "32px",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#fff", fontSize: "1.1rem",
            }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ padding: "24px" }}>
          {error && (
            <div style={{
              marginBottom: "16px", padding: "9px 14px", borderRadius: "10px",
              background: "#fef2f2", border: "1px solid #fecaca",
              color: "#dc2626", fontSize: "0.83rem", display: "flex", alignItems: "center", gap: "8px",
            }}>
              <i className="bi bi-exclamation-circle-fill"></i>{error}
            </div>
          )}

          {/* Konu */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.82rem", color: "#374151", marginBottom: "6px" }}>
              Konu <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <select
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              style={{
                width: "100%", padding: "10px 14px",
                border: "1.5px solid #e5e7eb", borderRadius: "10px",
                fontSize: "0.9rem", outline: "none", color: subjectId ? "#374151" : "#9ca3af",
                background: "#fafafa", cursor: "pointer",
              }}
            >
              <option value="">— Konu seçin —</option>
              {subjects.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Başlık */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.82rem", color: "#374151", marginBottom: "6px" }}>
              Başlık <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              type="text"
              placeholder="Destek talebinizin başlığını girin"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              style={{
                width: "100%", padding: "10px 14px", boxSizing: "border-box",
                border: "1.5px solid #e5e7eb", borderRadius: "10px",
                fontSize: "0.9rem", outline: "none", color: "#111827",
              }}
            />
          </div>

          {/* Not */}
          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.82rem", color: "#374151", marginBottom: "6px" }}>
              Açıklama / Not <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <textarea
              rows={3}
              placeholder="Sorunu veya isteğinizi detaylı açıklayın..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={{
                width: "100%", padding: "10px 14px", boxSizing: "border-box",
                border: "1.5px solid #e5e7eb", borderRadius: "10px",
                fontSize: "0.88rem", outline: "none", color: "#374151",
                resize: "vertical", fontFamily: "inherit",
              }}
            />
          </div>

          {/* Departman */}
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.82rem", color: "#374151", marginBottom: "6px" }}>
              Departman <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <select
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              style={{
                width: "100%", padding: "10px 14px",
                border: "1.5px solid #e5e7eb", borderRadius: "10px",
                fontSize: "0.9rem", outline: "none", color: "#374151",
                background: "#fafafa", cursor: "pointer",
              }}
            >
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>

          {/* Öncelik — Yıldızlar */}
          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.82rem", color: "#374151", marginBottom: "8px" }}>
              Öncelik
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {[1, 2, 3, 4].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setPriority(star)}
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(null)}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    padding: "2px", lineHeight: 1, transition: "transform 0.1s",
                    transform: hoveredStar !== null && star <= hoveredStar ? "scale(1.2)" : "scale(1)",
                  }}
                >
                  <i
                    className={star <= activeStar ? "bi bi-star-fill" : "bi bi-star"}
                    style={{
                      fontSize: "1.6rem",
                      color: star <= activeStar ? "#f59e0b" : "#d1d5db",
                      transition: "color 0.15s",
                    }}
                  ></i>
                </button>
              ))}
              <span style={{ marginLeft: "8px", fontSize: "0.8rem", color: "#6b7280", fontWeight: 600 }}>
                {["", "Düşük", "Orta", "Yüksek", "Kritik"][activeStar]}
              </span>
            </div>
          </div>

          {/* Görevler */}
          <div style={{ marginBottom: "24px" }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.82rem", color: "#374151", marginBottom: "8px" }}>
              Görevler
            </label>
            <button
              type="button"
              onClick={() => setShowTasksPopup(true)}
              style={{
                width: "100%", boxSizing: "border-box", display: "flex", alignItems: "center", gap: "10px",
                padding: "10px 14px", borderRadius: "10px",
                border: "1.5px solid #e5e7eb", background: "#fafafa",
                cursor: "pointer", textAlign: "left",
              }}
            >
              <i className="bi bi-list-check" style={{ color: "#6366f1", fontSize: "1rem", flexShrink: 0 }}></i>
              <span style={{ flex: 1, minWidth: 0, fontSize: "0.88rem", color: tasks.length ? "#111827" : "#9ca3af", fontWeight: tasks.length ? 600 : 400 }}>
                {tasks.length === 0 ? "Görev Ekle" : `${tasks.length} görev eklendi`}
              </span>
              <i className="bi bi-chevron-right" style={{ color: "#9ca3af", fontSize: "0.85rem", flexShrink: 0 }}></i>
            </button>
          </div>

          {/* Footer buttons */}
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <button
              onClick={onClose}
              disabled={saving}
              style={{
                padding: "10px 20px", borderRadius: "10px",
                border: "1.5px solid #e5e7eb", background: "#fff",
                color: "#374151", fontWeight: 600, fontSize: "0.88rem",
                cursor: "pointer",
              }}
            >
              İptal
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || !isFormValid}
              style={{
                padding: "10px 24px", borderRadius: "10px", border: "none",
                background: saving || !isFormValid
                  ? "#e5e7eb" : "linear-gradient(135deg, #6366f1, #4f46e5)",
                color: saving || !isFormValid ? "#9ca3af" : "#fff",
                fontWeight: 700, fontSize: "0.88rem",
                cursor: saving || !isFormValid ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", gap: "8px",
                transition: "opacity 0.15s",
              }}
            >
              {saving
                ? <><span className="spinner-border spinner-border-sm" role="status" /> Oluşturuluyor...</>
                : <><i className="bi bi-plus-circle-fill"></i> Ticket Oluştur</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>

    {showTasksPopup && (
      <div
        onClick={() => setShowTasksPopup(false)}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.55)",
          zIndex: 10000,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "16px",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff", borderRadius: "20px",
            width: "92%", maxWidth: "440px",
            maxHeight: "80vh", display: "flex", flexDirection: "column",
            boxShadow: "0 12px 48px rgba(0,0,0,0.22)",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div style={{
            padding: "18px 22px 16px", borderBottom: "1.5px solid #f1f5f9",
            display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
          }}>
            <div style={{ fontWeight: 800, fontSize: "0.98rem", color: "#111827", display: "flex", alignItems: "center", gap: "8px" }}>
              <i className="bi bi-list-check" style={{ color: "#6366f1" }}></i>
              Görevler
            </div>
            <button
              onClick={() => setShowTasksPopup(false)}
              style={{
                background: "#f3f4f6", border: "none",
                borderRadius: "8px", width: "30px", height: "30px",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: "#6b7280", fontSize: "1.05rem",
              }}
            >×</button>
          </div>

          {/* Add input */}
          <div style={{ padding: "16px 22px 0", flexShrink: 0 }}>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type="text"
                autoFocus
                value={taskInput}
                onChange={(e) => setTaskInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(); } }}
                placeholder="Görev ekle..."
                maxLength={200}
                style={{
                  flex: 1, minWidth: 0, padding: "9px 12px", boxSizing: "border-box",
                  border: "1.5px solid #e5e7eb", borderRadius: "10px",
                  fontSize: "0.88rem", outline: "none", color: "#111827",
                }}
              />
              <input
                type="number"
                min={1}
                value={taskWeightInput}
                onChange={(e) => setTaskWeightInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(); } }}
                placeholder="Ağırlık"
                title="Görev Ağırlığı — bu görevin ticketin tamamlanma yüzdesine ne kadar katkı yapacağını belirler"
                style={{
                  width: "78px", flexShrink: 0, padding: "9px 8px", boxSizing: "border-box",
                  border: "1.5px solid #e5e7eb", borderRadius: "10px",
                  fontSize: "0.88rem", outline: "none", color: "#111827", textAlign: "center",
                }}
              />
              <button
                type="button"
                onClick={addTask}
                disabled={!taskInput.trim()}
                style={{
                  padding: "0 16px", borderRadius: "10px", border: "none",
                  background: taskInput.trim() ? "#eef2ff" : "#f3f4f6",
                  color: taskInput.trim() ? "#4f46e5" : "#9ca3af",
                  fontWeight: 700, fontSize: "0.85rem",
                  cursor: taskInput.trim() ? "pointer" : "not-allowed",
                  display: "flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap", flexShrink: 0,
                }}
              >
                <i className="bi bi-plus-lg"></i>Ekle
              </button>
            </div>
            <div style={{ marginTop: "6px", display: "flex", alignItems: "flex-start", gap: "5px", fontSize: "0.72rem", color: "#9ca3af", lineHeight: 1.4 }}>
              <i className="bi bi-info-circle" style={{ flexShrink: 0, marginTop: "1px" }}></i>
              <span>Ağırlık: Bu görevin, ticketin tamamlanma yüzdesine ne kadar katkı yapacağını belirler. Ne kadar yüksekse etkisi o kadar fazla olur (varsayılan: 100).</span>
            </div>
          </div>

          {/* Scrollable list */}
          <div style={{ padding: "14px 22px", overflowY: "auto", flex: 1 }}>
            {tasks.length === 0 ? (
              <div style={{ textAlign: "center", color: "#9ca3af", padding: "24px 0", fontSize: "0.85rem" }}>
                <i className="bi bi-inbox" style={{ fontSize: "1.6rem", display: "block", marginBottom: "8px", opacity: 0.4 }}></i>
                Henüz görev eklenmedi.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                {tasks.map((task, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: "8px",
                    padding: "8px 12px", borderRadius: "9px",
                    background: "#f9fafb", border: "1px solid #f1f5f9",
                  }}>
                    <i className="bi bi-square" style={{ color: "#9ca3af", fontSize: "0.85rem", flexShrink: 0 }}></i>
                    <span style={{ flex: 1, minWidth: 0, fontSize: "0.85rem", color: "#374151", wordBreak: "break-word" }}>{task.title}</span>
                    <span
                      title="Görev Ağırlığı — bu görevin ticketin tamamlanma yüzdesine katkısı"
                      style={{
                        fontSize: "0.68rem", fontWeight: 700, color: "#4f46e5",
                        background: "#eef2ff", padding: "1px 8px", borderRadius: "20px", flexShrink: 0,
                      }}>
                      {task.weight}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeTask(i)}
                      style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", padding: 2, fontSize: "0.9rem", flexShrink: 0 }}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: "14px 22px 18px", borderTop: "1.5px solid #f1f5f9", display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
            <button
              onClick={() => setShowTasksPopup(false)}
              style={{
                padding: "9px 22px", borderRadius: "10px", border: "none",
                background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                color: "#fff", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
              }}
            >
              Tamam
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
